import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  X, Check, ArrowRight, ArrowLeft, Loader2, Pencil, Plus, Trash2, Share2, MapPin, Calendar, Award, TrendingUp, ClipboardList, Ticket, BookOpen,
  ShoppingBag, CloudSun, Compass, NotebookPen, ShieldCheck, Package, DollarSign, Star, BadgeCheck,
  Mountain, Wheat, Sun, Backpack, Tent, Truck, Home, MoreHorizontal,
} from "lucide-react";
import { api } from "@/lib/api";
import { useUI } from "@/context/UIContext";
import { useAuth } from "@/context/AuthContext";
import { STATE_HERO, STATE_HERO_DEFAULT, SPECIES_ICON, STATE_ABBR } from "@/lib/stateMeta";

// Weapon line-icons (Lucide lacks bow/rifle/muzzleloader). White stroke = currentColor.
const svg = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" };
const BowIcon = (p) => (<svg {...svg} width="22" height="22" {...p}><path d="M17 3c-6 4-6 14 0 18" /><path d="M17 3v18" /><path d="M4 12h13" /><path d="M4 12l3-2M4 12l3 2" /></svg>);
const RifleIcon = (p) => (<svg {...svg} width="22" height="22" {...p}><path d="M3 13h17" /><path d="M3 13v3h3l2-3" /><rect x="9" y="8" width="6" height="2" rx="1" /><path d="M10 10v1M14 10v1" /></svg>);
const MuzzleIcon = (p) => (<svg {...svg} width="22" height="22" {...p}><path d="M2 13h19" /><path d="M2 13v3h3l2-3" /><path d="M13 13v-3l2 1" /></svg>);

const SPECIES = [
  { key: "Elk", label: "Elk" }, { key: "Mule Deer", label: "Mule Deer" }, { key: "Whitetail Deer", label: "Whitetail Deer" },
  { key: "Black Bear", label: "Bear" }, { key: "Moose", label: "Moose" }, { key: "__more", label: "More" },
];
const METHODS = [["Archery", BowIcon], ["Rifle", RifleIcon], ["Muzzleloader", MuzzleIcon], ["Other", MoreHorizontal]];
const TERRAINS = [["Mountains + Timber", Mountain], ["Plains + Prairie", Wheat], ["Desert", Sun], ["Other", MoreHorizontal]];
const STYLES = [["Backcountry", Backpack], ["Base Camp", Tent], ["Road Access", Truck], ["Lodge / Outfitted", Home]];
const DAYS = ["3", "5", "7", "10+"];
const BUDGETS = [["best_value", "Best Value"], ["balanced", "Balanced"], ["premium", "Premium"]];
const STATES = Object.keys(STATE_ABBR).sort();
const huntTitle = (f) => `${f.jurisdiction} ${f.method} ${f.species.replace(" Deer", "")} Hunt`;

const Steps = ({ step }) => (
  <div className="flex items-center gap-2 mb-7" data-testid="bmh-steps">
    {[1, 2, 3, 4].map((n) => (
      <div key={n} className="flex items-center gap-2">
        <span className={`w-6 h-6 rounded-full grid place-items-center text-xs font-bold ${step === n ? "bg-blaze text-white" : step > n ? "bg-blaze/30 text-bone" : "border border-bone/25 text-bone/50"}`}>{n}</span>
        {n < 4 && <span className="w-8 h-px bg-bone/20" />}
      </div>
    ))}
  </div>
);
const Overline = () => <span className="hr-overline text-blaze text-[10px]">Build My Hunt</span>;
const Tile = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`flex flex-col items-center justify-center gap-2 py-4 px-2 rounded-md border text-sm font-medium transition-all ${active ? "border-blaze bg-blaze/15 text-bone" : "border-bone/15 bg-bone/[0.03] text-bone/75 hover:border-bone/40"}`}>{children}</button>
);

export function BuildHuntModal() {
  const { buildHuntOpen, setBuildHuntOpen, setAiContext, openAuth, dashboardHuntId, setDashboardHuntId } = useUI();
  const { isAuthed } = useAuth();
  const navigate = useNavigate();
  const DEFAULTS = { species: "Elk", jurisdiction: "Idaho", method: "Archery", terrain: "Mountains + Timber", hunt_style: "Backcountry", duration_days: "7", budget_mode: "best_value" };
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [hunt, setHunt] = useState(null);
  const [allHunts, setAllHunts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [f, setF] = useState(DEFAULTS);

  const fromHunt = (h) => ({
    species: h.species || "Elk", jurisdiction: h.jurisdiction || "Idaho", method: h.method || "Archery",
    terrain: h.terrain || "Mountains + Timber", hunt_style: h.hunt_style || "Backcountry",
    duration_days: h.duration_days >= 10 ? "10+" : (h.duration_days ? String(h.duration_days) : "7"),
    budget_mode: h.budget_mode || "best_value",
  });
  const byRecent = (list) => (list || []).slice().sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));

  const loadHunt = async (h) => {
    const nf = fromHunt(h);
    setF(nf);
    setHunt(h);
    setAiContext({ state: nf.jurisdiction, species: nf.species, weapon: nf.method, hunt_style: nf.hunt_style, page: "build" });
    try { setSummary(await api.huntSummary()); } catch { /* optional */ }
    setStep(4);
  };

  // Rehydrate saved hunts on open so selections persist across refresh. If a specific hunt
  // was requested (from the HomeRange AI page "Your Hunts" card), open straight to it.
  useEffect(() => {
    if (!buildHuntOpen || loaded) return;
    if (!isAuthed) { setLoaded(true); return; }
    (async () => {
      try {
        const hunts = byRecent(await api.hunts());
        setAllHunts(hunts);
        const target = (dashboardHuntId && hunts.find((h) => h.id === dashboardHuntId)) || hunts[0];
        if (target) await loadHunt(target);
      } catch { /* not signed in / no hunts */ } finally { setLoaded(true); }
    })();
  }, [buildHuntOpen, isAuthed, loaded, dashboardHuntId, setAiContext]);

  // Reset rehydration + specific-hunt target when the modal closes, so reopening is fresh.
  useEffect(() => {
    if (!buildHuntOpen) { setLoaded(false); if (dashboardHuntId) setDashboardHuntId(null); }
  }, [buildHuntOpen, dashboardHuntId, setDashboardHuntId]);

  const close = () => setBuildHuntOpen(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const startNew = () => { setF(DEFAULTS); setHunt(null); setSummary(null); setStep(1); };
  const switchHunt = (id) => { const h = allHunts.find((x) => x.id === id); if (h) loadHunt(h); };
  const removeHunt = async () => {
    if (!hunt?.id || !window.confirm("Delete this hunt? This can't be undone.")) return;
    try {
      await api.deleteHunt(hunt.id);
      const hunts = byRecent(await api.hunts());
      setAllHunts(hunts);
      if (hunts[0]) await loadHunt(hunts[0]); else startNew();
      toast.success("Hunt deleted");
    } catch { toast.error("Could not delete hunt."); }
  };

  const build = async () => {
    if (!isAuthed) { setBuildHuntOpen(false); openAuth("register"); toast.message("Create a free account to save your hunt."); return; }
    setBusy(true);
    const title = huntTitle(f);
    try {
      const h = await api.createHunt({
        title, species: f.species, jurisdiction: f.jurisdiction, method: f.method, terrain: f.terrain,
        hunt_style: f.hunt_style, duration_days: parseInt(f.duration_days, 10) || 7, budget_mode: f.budget_mode, status: "planning",
      });
      setHunt({ ...h, title });
      setAiContext({ state: f.jurisdiction, species: f.species, weapon: f.method, hunt_style: f.hunt_style, page: "build" });
      try { localStorage.setItem("hr_active_hunt", h.id); } catch { /* ignore */ }
      // After building, go to the full-page HomeRange AI destination (not the white sidebar).
      setBuildHuntOpen(false);
      navigate("/hunt-assistant");
    } catch { toast.error("Could not create hunt."); } finally { setBusy(false); }
  };

  // Functional readiness — 6 binary milestones + a FRACTIONAL gear score, never hardcoded.
  const gearOwned = hunt?.gear_owned ?? (summary?.hunt?.gear_remaining != null ? Math.max(0, 9 - summary.hunt.gear_remaining) : 0);
  const gearTotal = hunt?.gear_total ?? 9;
  const gearFrac = gearTotal ? gearOwned / gearTotal : 0;
  const checklist = [
    { label: "Hunt profile created", done: !!hunt },
    { label: "Regulations identified", done: !!f.jurisdiction },
    { label: "Season information", done: !!f.method },
    { label: "Unit selected", done: !!hunt?.area },
    { label: "Tag / draw status", done: !!hunt?.draw_status },
    { label: `Gear list (${gearOwned}/${gearTotal})`, done: gearFrac >= 1, gear: true },
    { label: "Dates selected", done: !!hunt?.start_date },
  ];
  const binaryDone = checklist.filter((c) => !c.gear && c.done).length;
  const readiness = Math.round(((binaryDone + gearFrac) / 7) * 100);
  const heroImg = STATE_HERO[f.jurisdiction] || STATE_HERO_DEFAULT;

  // Live Readiness — persist Unit / Draw status / Dates to the hunt so the checklist ticks itself.
  const [savingField, setSavingField] = useState(false);
  const saveField = async (patch) => {
    if (!hunt?.id) return;
    setSavingField(true);
    try {
      const updated = await api.updateHunt(hunt.id, patch);
      setHunt(updated);
      setAllHunts((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      try { setSummary(await api.huntSummary()); } catch { /* optional */ }
    } catch { toast.error("Could not save."); } finally { setSavingField(false); }
  };

  // Unit Recommendations — real verified draw records for this state + species (top by odds).
  const [recs, setRecs] = useState(null); // null = loading, [] = none, [..] = list
  useEffect(() => {
    if (step !== 4) return;
    let active = true;
    setRecs(null);
    api.exploreHunts(f.jurisdiction, { species: f.species })
      .then((d) => { if (active) setRecs((d.hunts || []).slice(0, 3)); })
      .catch(() => { if (active) setRecs([]); });
    return () => { active = false; };
  }, [step, f.jurisdiction, f.species]);

  // AI Partner Nudges — verified HomeRange partners (outfitters/guides + gear brands) for this hunt.
  const [partners, setPartners] = useState(null);
  useEffect(() => {
    if (step !== 4) return;
    let active = true;
    setPartners(null);
    api.partnerSuggest({ state: f.jurisdiction, species: f.species, limit: 4 })
      .then((d) => { if (active) setPartners(d.partners || []); })
      .catch(() => { if (active) setPartners([]); });
    return () => { active = false; };
  }, [step, f.jurisdiction, f.species]);

  // Share My Hunt — opt-in public link (basics only).
  const doShare = async () => {
    if (!hunt?.id) return;
    try {
      const { share_id } = await api.shareHunt(hunt.id);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/hunts/shared/${share_id}/card`;
      if (navigator.share) {
        try { await navigator.share({ title: hunt.title, text: "Check out my hunt on HomeRange", url }); return; }
        catch (e) { if (e?.name === "AbortError") return; }
      }
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied to clipboard");
    } catch { toast.error("Could not create share link."); }
  };

  const NAV = [
    { icon: ClipboardList, label: "Overview", sub: "See your hunt summary", to: "/account" },
    { icon: Ticket, label: "Draw & Tag", sub: "Odds, points, deadlines", to: `/states/${encodeURIComponent(f.jurisdiction)}` },
    { icon: BookOpen, label: "Regulations", sub: "Seasons, rules, restrictions", to: `/states/${encodeURIComponent(f.jurisdiction)}` },
    { icon: ShoppingBag, label: "Gear", sub: "Recommended gear list", to: "/marketplace" },
    { icon: CloudSun, label: "Conditions", sub: "Weather, terrain, insights", to: `/conditions/${encodeURIComponent(f.jurisdiction)}` },
    { icon: Compass, label: "Outfitters", sub: "Guides & services", to: "/outfitters" },
    { icon: NotebookPen, label: "Field Notes", sub: "Plan, save, document", to: "/field-notes" },
  ];
  const goNav = (to) => { close(); navigate(to); };
  const goRec = () => { close(); navigate(`/states/${encodeURIComponent(f.jurisdiction)}/species/${encodeURIComponent(f.species)}`); };
  const goAI = () => {
    setAiContext({ state: f.jurisdiction, species: f.species, weapon: f.method, hunt_style: f.hunt_style, page: "build" });
    if (hunt?.id) { try { localStorage.setItem("hr_active_hunt", hunt.id); } catch { /* ignore */ } }
    close();
    navigate("/hunt-assistant");
  };

  return (
    <AnimatePresence>
      {buildHuntOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[75] bg-[#0d1410] grid sm:place-items-center p-0 sm:p-6 overflow-y-auto" data-testid="build-hunt-modal">
          <div className="w-full max-w-5xl bg-[#12190f] border border-bone/10 rounded-none sm:rounded-lg text-bone relative min-h-full sm:min-h-0 my-0 sm:my-auto grain">
            <button onClick={close} className="absolute top-4 right-4 text-bone/50 hover:text-bone z-10 p-1" aria-label="Close" data-testid="build-close"><X size={24} /></button>

            {step < 4 ? (
              <div className="p-5 sm:p-10 max-w-2xl">
                <Steps step={step} />
                <Overline />

                {step === 1 && (<div data-testid="bmh-step-1">
                  <h2 className="font-serif text-4xl sm:text-5xl leading-[0.95] mt-2">What are<br />you hunting?</h2>
                  <p className="text-bone/60 mt-3 text-sm">Choose your species and state.</p>
                  <p className="hr-overline text-bone/50 mt-6 mb-3">Species</p>
                  <div className="grid grid-cols-3 gap-3">
                    {SPECIES.map((s) => (
                      <Tile key={s.key} active={f.species === s.key} onClick={() => s.key !== "__more" && set("species", s.key)} testid={`bmh-species-${s.label}`}>
                        {SPECIES_ICON[s.key] ? <img src={SPECIES_ICON[s.key]} alt="" className="w-9 h-9 object-contain opacity-90" /> : <span className="text-bone/50 text-lg">•••</span>}
                        {s.label}
                      </Tile>
                    ))}
                  </div>
                  <p className="hr-overline text-bone/50 mt-6 mb-2">State / Province</p>
                  <select value={f.jurisdiction} onChange={(e) => set("jurisdiction", e.target.value)} className="w-full bg-bone/[0.04] border border-bone/20 rounded-md px-4 py-3 text-bone outline-none focus:border-blaze" data-testid="bmh-state">
                    {STATES.map((s) => <option key={s} value={s} className="text-charcoal">{s}</option>)}
                  </select>
                  <button onClick={() => setStep(2)} className="w-full bg-blaze text-white py-3.5 rounded-md font-semibold mt-6 inline-flex items-center justify-center gap-2 hover:bg-blaze-light transition-colors" data-testid="bmh-continue-1">Continue <ArrowRight size={16} /></button>
                </div>)}

                {step === 2 && (<div data-testid="bmh-step-2">
                  <h2 className="font-serif text-4xl sm:text-5xl leading-[0.95] mt-2">How are you<br />planning to hunt?</h2>
                  <p className="text-bone/60 mt-3 text-sm">Tell us about your approach.</p>
                  <p className="hr-overline text-bone/50 mt-6 mb-3">Method</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {METHODS.map(([m, Ic]) => <Tile key={m} active={f.method === m} onClick={() => set("method", m)} testid={`bmh-method-${m}`}><Ic size={22} className="opacity-85" /><span className="text-center leading-tight text-xs">{m}</span></Tile>)}
                  </div>
                  <p className="hr-overline text-bone/50 mt-6 mb-3">Terrain</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {TERRAINS.map(([t, Ic]) => <Tile key={t} active={f.terrain === t} onClick={() => set("terrain", t)} testid={`bmh-terrain-${t}`}><Ic size={22} className="opacity-85" /><span className="text-center leading-tight text-xs">{t}</span></Tile>)}
                  </div>
                  <p className="hr-overline text-bone/50 mt-6 mb-3">Hunt Style</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {STYLES.map(([s, Ic]) => <Tile key={s} active={f.hunt_style === s} onClick={() => set("hunt_style", s)} testid={`bmh-style-${s}`}><Ic size={22} className="opacity-85" /><span className="text-center leading-tight text-xs">{s}</span></Tile>)}
                  </div>
                  <div className="flex items-center justify-between mt-7">
                    <button onClick={() => setStep(1)} className="inline-flex items-center gap-2 text-bone/70 hover:text-bone text-sm font-semibold" data-testid="bmh-back-2"><ArrowLeft size={16} /> Back</button>
                    <button onClick={() => setStep(3)} className="bg-blaze text-white px-8 py-3 rounded-md font-semibold inline-flex items-center gap-2 hover:bg-blaze-light transition-colors" data-testid="bmh-continue-2">Continue <ArrowRight size={16} /></button>
                  </div>
                </div>)}

                {step === 3 && (<div data-testid="bmh-step-3">
                  <h2 className="font-serif text-4xl sm:text-5xl leading-[0.95] mt-2">Build around you.</h2>
                  <p className="text-bone/60 mt-3 text-sm">A few final details to complete your hunt.</p>
                  <p className="hr-overline text-bone/50 mt-6 mb-3">Days in Field</p>
                  <div className="grid grid-cols-4 gap-3">
                    {DAYS.map((d) => <Tile key={d} active={f.duration_days === d} onClick={() => set("duration_days", d)} testid={`bmh-days-${d}`}><span className="text-lg font-serif">{d}</span></Tile>)}
                  </div>
                  <p className="hr-overline text-bone/50 mt-6 mb-3">Budget Mode</p>
                  <div className="grid grid-cols-3 gap-3">
                    {BUDGETS.map(([v, l]) => <Tile key={v} active={f.budget_mode === v} onClick={() => set("budget_mode", v)} testid={`bmh-budget-${v}`}>{l}</Tile>)}
                  </div>
                  <div className="border border-bone/15 rounded-md p-4 mt-6">
                    <p className="text-sm font-semibold text-bone">HomeRange will also check:</p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3 text-xs text-bone/70">
                      {[[Package, "Gear Locker"], [CloudSun, "Hunt conditions"], [DollarSign, "Product value"], [Star, "Reviews"], [DollarSign, "Current pricing"], [BookOpen, "Official resources"]].map(([Ic, l]) => (
                        <span key={l} className="inline-flex items-center gap-1.5"><Ic size={13} className="text-blaze" /> {l}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-7">
                    <button onClick={() => setStep(2)} className="inline-flex items-center gap-2 text-bone/70 hover:text-bone text-sm font-semibold" data-testid="bmh-back-3"><ArrowLeft size={16} /> Back</button>
                    <button onClick={build} disabled={busy} className="bg-blaze text-white px-8 py-3 rounded-md font-semibold inline-flex items-center gap-2 hover:bg-blaze-light transition-colors disabled:opacity-60" data-testid="bmh-build">{busy && <Loader2 size={15} className="animate-spin" />} Build My Hunt</button>
                  </div>
                </div>)}
              </div>
            ) : (
              /* Step 4 — completed dashboard */
              <div className="p-5 sm:p-10" data-testid="bmh-dashboard">
                {allHunts.length > 1 && (
                  <div className="flex items-center gap-2 flex-wrap mb-5" data-testid="bmh-hunt-switcher">
                    <span className="hr-overline text-bone/40 mr-1">My hunts</span>
                    {allHunts.map((h) => (
                      <button key={h.id} onClick={() => switchHunt(h.id)} data-testid={`bmh-switch-${h.id}`}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${hunt?.id === h.id ? "bg-blaze text-white border-blaze" : "border-bone/20 text-bone/70 hover:border-blaze hover:text-blaze"}`}>
                        {h.title}
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <span className="inline-flex items-center gap-2 text-sm text-forest-light text-[#7FB88A]"><ShieldCheck size={16} /> Your hunt is ready.</span>
                    <h2 className="font-serif text-4xl sm:text-5xl mt-1" data-testid="bmh-hunt-title">{hunt?.title || huntTitle(f)}</h2>
                  </div>
                  <div className="flex items-center gap-3 sm:gap-4 shrink-0 flex-wrap justify-end">
                    <button onClick={doShare} className="inline-flex items-center gap-1.5 text-sm text-bone/70 hover:text-blaze" data-testid="bmh-share"><Share2 size={14} /> Share</button>
                    <button onClick={() => setStep(1)} className="inline-flex items-center gap-1.5 text-sm text-bone/70 hover:text-blaze" data-testid="bmh-edit"><Pencil size={14} /> Edit</button>
                    <button onClick={startNew} className="inline-flex items-center gap-1.5 text-sm text-bone/70 hover:text-blaze" data-testid="bmh-start-new"><Plus size={14} /> New</button>
                    <button onClick={removeHunt} className="inline-flex items-center gap-1.5 text-sm text-bone/40 hover:text-red-400" data-testid="bmh-delete"><Trash2 size={14} /> Delete</button>
                  </div>
                </div>

                <div className="relative h-52 sm:h-64 rounded-md overflow-hidden mt-5">
                  <img src={heroImg} alt={`${f.jurisdiction} landscape`} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#12190f]/60 to-transparent" />
                </div>

                <div className="grid md:grid-cols-2 gap-6 mt-6">
                  <div>
                    <p className="hr-overline text-bone/50">Hunt Readiness</p>
                    <div className="flex items-center gap-4 mt-2">
                      <span className="font-serif text-5xl text-bone tabular-nums" data-testid="bmh-readiness">{readiness}%</span>
                      <div className="flex-1 flex gap-1">
                        {checklist.map((c, i) => <span key={i} className={`h-2 flex-1 rounded-sm ${c.done ? "bg-blaze" : "bg-bone/15"}`} />)}
                      </div>
                    </div>
                  </div>
                  <ul className="space-y-1.5" data-testid="bmh-checklist">
                    {checklist.map((c) => (
                      <li key={c.label} className="flex items-center gap-2 text-sm">
                        <span className={`w-4 h-4 rounded-sm grid place-items-center ${c.done ? "bg-blaze text-white" : "border border-bone/30"}`}>{c.done && <Check size={11} />}</span>
                        <span className={c.done ? "text-bone/85" : "text-bone/50"}>{c.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="border border-bone/15 rounded-md p-4 mt-6" data-testid="bmh-trip-details">
                  <p className="hr-overline text-bone/50 mb-3 flex items-center gap-2">Complete your hunt {savingField && <Loader2 size={12} className="animate-spin text-blaze" />}</p>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <label className="block">
                      <span className="text-[11px] text-bone/55 flex items-center gap-1.5 mb-1.5"><MapPin size={12} className="text-blaze" /> Unit / Area</span>
                      <input key={`unit-${hunt?.id}`} defaultValue={hunt?.area || ""} placeholder="e.g. Unit 12"
                        onBlur={(e) => e.target.value !== (hunt?.area || "") && saveField({ area: e.target.value })}
                        className="w-full bg-bone/[0.04] border border-bone/20 rounded-md px-3 py-2.5 text-sm text-bone outline-none focus:border-blaze" data-testid="bmh-field-unit" />
                    </label>
                    <label className="block">
                      <span className="text-[11px] text-bone/55 flex items-center gap-1.5 mb-1.5"><Award size={12} className="text-blaze" /> Tag / Draw status</span>
                      <select value={hunt?.draw_status || ""} onChange={(e) => saveField({ draw_status: e.target.value })}
                        className="w-full bg-bone/[0.04] border border-bone/20 rounded-md px-3 py-2.5 text-sm text-bone outline-none focus:border-blaze" data-testid="bmh-field-draw">
                        <option value="" className="text-charcoal">Not set</option>
                        <option className="text-charcoal">Not applied</option>
                        <option className="text-charcoal">Applied</option>
                        <option className="text-charcoal">Drawn</option>
                        <option className="text-charcoal">OTC / Over-the-counter</option>
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-[11px] text-bone/55 flex items-center gap-1.5 mb-1.5"><Calendar size={12} className="text-blaze" /> Hunt dates</span>
                      <input type="date" value={hunt?.start_date || ""} onChange={(e) => saveField({ start_date: e.target.value })}
                        className="w-full bg-bone/[0.04] border border-bone/20 rounded-md px-3 py-2.5 text-sm text-bone outline-none focus:border-blaze" data-testid="bmh-field-dates" />
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-3 sm:grid-cols-7 gap-2 mt-7">
                  {NAV.map((n) => { const Ic = n.icon; return (
                    <button key={n.label} onClick={() => goNav(n.to)} className="border border-bone/12 rounded-md p-3 text-left hover:border-blaze hover:bg-bone/[0.04] transition-colors" data-testid={`bmh-nav-${n.label}`}>
                      <Ic size={18} className="text-blaze" />
                      <p className="text-[11px] font-bold uppercase tracking-wide mt-2 text-bone/90">{n.label}</p>
                      <p className="text-[10px] text-bone/45 leading-tight mt-0.5">{n.sub}</p>
                    </button>
                  ); })}
                </div>

                <p className="hr-overline text-bone/50 mt-8">{`What's next? — Top units for your hunt`}</p>
                <div className="mt-3" data-testid="bmh-unit-recs">
                  {recs === null && (
                    <div className="flex items-center gap-2 text-sm text-bone/50 py-4"><Loader2 size={15} className="animate-spin text-blaze" /> Finding verified units in {f.jurisdiction}…</div>
                  )}
                  {recs !== null && recs.length > 0 && (
                    <div className="grid sm:grid-cols-3 gap-3">
                      {recs.map((r, i) => (
                        <button key={r.id || i} onClick={goRec} className="text-left border border-bone/15 rounded-md p-4 hover:border-blaze hover:bg-bone/[0.04] transition-colors" data-testid={`bmh-rec-${i}`}>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-semibold text-bone truncate">{r.unit || "Statewide"}</span>
                            <span className="font-serif text-2xl text-blaze tabular-nums shrink-0">{r.draw_odds_pct != null ? `${r.draw_odds_pct}%` : "—"}</span>
                          </div>
                          <p className="text-[11px] text-bone/50 mt-0.5">{[r.weapon, r.residency, r.season].filter(Boolean).join(" · ") || "Draw odds"}</p>
                          <div className="flex items-center gap-3 mt-3 text-[11px] text-bone/60">
                            <span className="inline-flex items-center gap-1"><Award size={11} className="text-blaze" /> {r.min_points != null ? `${r.min_points} pts` : "No points"}</span>
                            <span className="inline-flex items-center gap-1"><TrendingUp size={11} className="text-blaze" /> {r.point_creep || "Stable"}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {recs !== null && recs.length === 0 && (
                    <div className="border border-dashed border-bone/20 rounded-md p-5 text-sm text-bone/55" data-testid="bmh-recs-empty">
                      Verified draw-odds coverage for {f.species} in {f.jurisdiction} is in progress — no unit numbers are shown until they{"\u2019"}re confirmed from the official source. Ask HomeRange AI below for current guidance.
                    </div>
                  )}
                </div>

                {partners !== null && partners.length > 0 && (
                  <>
                    <p className="hr-overline text-bone/50 mt-8">{`HomeRange partners for your ${f.jurisdiction} hunt`}</p>
                    <div className="grid sm:grid-cols-2 gap-3 mt-3" data-testid="bmh-partner-nudges">
                      {partners.map((p) => (
                        <button key={p.id} onClick={() => { close(); navigate(p.public_path); }}
                          className="text-left border border-bone/15 rounded-md p-4 hover:border-blaze hover:bg-bone/[0.04] transition-colors flex items-center gap-3" data-testid={`bmh-partner-${p.slug}`}>
                          <div className="w-11 h-11 rounded-lg bg-bone/10 overflow-hidden grid place-items-center shrink-0">
                            {p.logo ? <img src={p.logo} alt={p.name} className="w-full h-full object-cover" /> : <span className="font-serif text-lg text-bone">{(p.name || "H")[0]}</span>}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm font-semibold text-bone truncate">{p.name}</span>
                              {(p.verification_status || "").startsWith("verified") && <BadgeCheck size={13} className="text-blaze shrink-0" />}
                            </div>
                            <p className="text-[11px] text-bone/50 mt-0.5 capitalize">{p.partner_type}{p.location ? ` · ${p.location}` : ""}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                    <p className="text-[11px] text-bone/40 mt-2">Verified HomeRange partners — always confirm regulations with the official state source.</p>
                  </>
                )}

                <div className="border border-bone/15 rounded-md p-5 mt-4 flex items-center gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-xl text-bone">{`Keep planning with HomeRange AI`}</p>
                    <p className="text-sm text-bone/55 mt-1">{`Get a gear plan, season timing and unit strategy tailored to this hunt.`}</p>
                  </div>
                  <button onClick={goAI} className="bg-blaze text-white px-5 py-3 rounded-md text-sm font-semibold inline-flex items-center gap-2 hover:bg-blaze-light transition-colors shrink-0" data-testid="bmh-continue-planning">Continue Planning <ArrowRight size={15} /></button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
